import { useEffect, useState } from "react";
import { X, CheckSquare } from "lucide-react";
import api, { mediaUrl } from "@/lib/api";
import { Loader } from "@/components/common/Loader";
import { PageHeader, StatusBadge } from "@/components/dashboard/ui";
import { PhotoMasonry } from "@/components/gallery/PhotoMasonry";
import { Lightbox } from "@/components/gallery/Lightbox";

export default function AdminEvents() {
  const [list, setList] = useState(null);
  const [modal, setModal] = useState(null); // {event, photos}
  const [loadingSel, setLoadingSel] = useState(false);
  const [idx, setIdx] = useState(null);

  useEffect(() => { api.get("/admin/events").then((r) => setList(r.data.events)).catch(() => setList([])); }, []);
  if (!list) return <Loader />;

  const viewSelected = async (e) => {
    setLoadingSel(true); setModal({ event: e, photos: [] });
    try { const r = await api.get(`/admin/events/${e.id}/selected`); setModal({ event: e, photos: r.data.photos }); }
    catch { setModal({ event: e, photos: [] }); } finally { setLoadingSel(false); }
  };

  return (
    <div>
      <PageHeader title="All Events" subtitle={`${list.length} events across the platform`} />
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {list.map((e) => (
          <div key={e.id} data-testid={`admin-event-${e.id}`} className="rounded-2xl overflow-hidden border border-border bg-black/40 hover-lift">
            <div className="relative aspect-[16/9] bg-secondary">
              {e.cover_image && <img src={mediaUrl(e.cover_image)} alt="" className="w-full h-full object-cover" />}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
              <div className="absolute bottom-3 left-4 right-4">
                <div className="text-white font-medium">{e.name}</div>
                <div className="text-xs text-white/70">{e.photographer_name} · {e.event_type}</div>
              </div>
            </div>
            <div className="p-4">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{e.photo_count} photos · {e.views} views</span>
                <StatusBadge status={e.selection_status} />
              </div>
              <button data-testid={`admin-view-selected-${e.id}`} onClick={() => viewSelected(e)}
                className="mt-3 w-full inline-flex items-center justify-center gap-2 glass rounded-full py-2 text-sm text-white hover:border-gold/40">
                <CheckSquare className="w-4 h-4 text-gold" /> View selected ({e.selected_count || 0})
              </button>
            </div>
          </div>
        ))}
      </div>

      {modal && (
        <div className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm p-4 sm:p-10 overflow-y-auto" data-testid="admin-selected-modal">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <div><h2 className="font-serif text-3xl text-white font-light">{modal.event.name}</h2><p className="text-sm text-muted-foreground">Client-selected photos · {modal.event.client_name}</p></div>
              <button data-testid="close-admin-selected" onClick={() => setModal(null)} className="w-11 h-11 rounded-full glass flex items-center justify-center text-white"><X className="w-5 h-5" /></button>
            </div>
            {loadingSel ? <Loader label="Loading selected photos" /> :
              modal.photos.length === 0 ? <p className="text-center text-muted-foreground py-20">No photos selected by this client yet.</p> :
                <PhotoMasonry photos={modal.photos} onOpen={setIdx} watermark={modal.event.watermark} />}
            <Lightbox photos={modal.photos} index={idx} onClose={() => setIdx(null)} setIndex={setIdx} downloadable watermark={modal.event.watermark} />
          </div>
        </div>
      )}
    </div>
  );
}
